function normalizeText(value = '') {
  return String(value)
    .toLocaleLowerCase()
    .normalize('NFKD')
    .replace(/[\u064B-\u065F\u0670]/g, '')
    .replace(/[أإآ]/g, 'ا')
    .replace(/ة/g, 'ه')
    .replace(/ى/g, 'ي')
    .replace(/[ؤئ]/g, 'ء')
    .replace(/[^\p{L}\p{N}\s]/gu, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function levenshtein(left, right) {
  if (!left) return right.length;
  if (!right) return left.length;
  const previous = Array.from({ length: right.length + 1 }, (_, index) => index);
  for (let i = 1; i <= left.length; i += 1) {
    const current = [i];
    for (let j = 1; j <= right.length; j += 1) {
      current[j] = Math.min(
        current[j - 1] + 1,
        previous[j] + 1,
        previous[j - 1] + (left[i - 1] === right[j - 1] ? 0 : 1),
      );
    }
    previous.splice(0, previous.length, ...current);
  }
  return previous[right.length];
}

function tokenScore(query, candidate) {
  if (!query || !candidate) return 0;
  if (query === candidate) return 1;
  if (query.includes(candidate) || candidate.includes(query)) {
    return 0.78 + Math.min(query.length, candidate.length) / Math.max(query.length, candidate.length) * 0.22;
  }
  return Math.max(0, 1 - levenshtein(query, candidate) / Math.max(query.length, candidate.length));
}

export function scoreRestaurantText(input, restaurant) {
  const query = normalizeText(input);
  if (!query) return 0;
  const candidates = [restaurant.name, restaurant.nameAr, ...(restaurant.aliases || [])]
    .map(normalizeText)
    .filter(Boolean);
  const queryWords = query.split(' ').filter((word) => word.length > 1);
  let best = 0;

  for (const candidate of candidates) {
    best = Math.max(best, tokenScore(query, candidate));
    const candidateWords = candidate.split(' ').filter((word) => word.length > 1);
    if (queryWords.length && candidateWords.length) {
      const matched = queryWords.filter((word) =>
        candidateWords.some((candidateWord) => tokenScore(word, candidateWord) >= 0.72),
      ).length;
      best = Math.max(best, matched / Math.max(queryWords.length, candidateWords.length));
    }
  }
  return Math.max(0, Math.min(1, best));
}

export function rankRestaurants(restaurants, detectedText, visualScores = {}, manual = false) {
  return restaurants
    .map((restaurant) => {
      const textScore = scoreRestaurantText(detectedText, restaurant);
      const visualScore = visualScores[restaurant.id] || 0;
      let confidence;
      if (manual) confidence = textScore;
      else if (textScore > 0 && visualScore > 0) {
        confidence = textScore * 0.58 + visualScore * 0.42;
        if (textScore >= 0.58 && visualScore >= 0.58) confidence += 0.08;
      } else if (textScore > 0) confidence = textScore * 0.82;
      else confidence = visualScore * 0.76;

      const evidence = [];
      if (textScore >= 0.45) evidence.push('name');
      if (visualScore >= 0.5) evidence.push('image');
      return {
        ...restaurant,
        textScore,
        visualScore,
        confidence: Math.max(0, Math.min(1, confidence)),
        evidence,
      };
    })
    .filter((restaurant) => restaurant.confidence >= (manual ? 0.22 : 0.24))
    .sort((a, b) => b.confidence - a.confidence)
    .slice(0, 8);
}

export function getTextCandidateIds(restaurants, text, ocrConfidence = 0) {
  const ranked = restaurants
    .map((restaurant) => ({ id: restaurant.id, score: scoreRestaurantText(text, restaurant) }))
    .sort((a, b) => b.score - a.score);

  const best = ranked[0]?.score || 0;
  const second = ranked[1]?.score || 0;

  // Never let weak or noisy OCR exclude the correct visual match.
  // Candidate narrowing is used only when OCR is clearly reliable and distinctive.
  if (ocrConfidence < 0.45 || best < 0.58 || best - second < 0.08) return null;

  const useful = ranked
    .filter((item) => item.score >= Math.max(0.42, best - 0.24))
    .slice(0, 20)
    .map((item) => item.id);
  return useful.length ? useful : null;
}
